import {
  BrowserRouter,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import './App.scss';
import { ThemeProvider } from '@mui/material/styles';
import Box from '@mui/material/Box';
import { useMemo, useState } from 'react';
import Login from './pages/Login/Login';
import Register from './pages/Register/Register';
import BugPanel from './components/BugPanel/BugPanel';
import Footer from './components/Footer/Footer';
import theme from './config/theme';
import NotFound from './pages/General/NotFound';
import { DataProvider } from './context/DataContext';
import { DashboardDataProvider } from './context/DashboardDataContext';
import Loading from './components/Loading/Loading';
import Messagebar from './components/Messagebar/Messagebar';
import Inventory from './pages/Dashboard/Inventory/Inventory';
import Home from './pages/Dashboard/Home/Home';
import Dashboard from './pages/Dashboard/Dashboard/Dashboard';
import Integrations from './pages/Dashboard/Integrations/Integrations';
import Organisation from './pages/Dashboard/Organisation/Organisation';
// import Administration from './pages/Dashboard/Administration/Administration';
import Recommendation from './pages/Dashboard/Recommendation/Recommendation';
import { AuthContext } from './context/AuthContext';
import { getAuthTokensFromBrowser } from './utils/utils';
import Sidebar from './components/Sidebar/Sidebar';
import ProtectedRoute from './components/ProtectedRoute/ProtectedRoute';
import ForgotPassword from './pages/ForgotPassword/ForgotPassword';
import VerifyEmail from './pages/VerifyEmail/VerifyEmail';
import NoLicense from './pages/NoLicense/NoLicense';
import { LocationProvider } from './context/LocationContext';
import SavedReports from './pages/Dashboard/SavedReports/SavedReports';
import SmartMetering from './pages/Dashboard/SmartMetering';

const PUBLIC_ROUTES = ['/', '/register', '/forgot-password', '/verify-email'];

function AppContent({
  showSidebar,
  shouldShowSidebar,
  setShowSidebar,
  environment,
}: {
  showSidebar: boolean;
  shouldShowSidebar: boolean;
  setShowSidebar: (v: boolean) => void;
  environment: string | undefined;
}) {
  const location = useLocation();
  const isPublicRoute = PUBLIC_ROUTES.includes(location.pathname);

  return (
    <div className="App">
      <Box sx={{ height: 'calc(100vh - 40px)', display: 'flex' }}>
        <Box sx={{ height: 'calc(100vh - 50px)' }}>
          {!isPublicRoute && (showSidebar || shouldShowSidebar) && (
            <Sidebar logout={() => setShowSidebar(false)} />
          )}
        </Box>
        <Box
          sx={{
            height: 'calc(100vh - 40px)',
            width: '100%',
            backgroundColor: 'rgb(230,230,230)',
            overflow: 'auto',
          }}
        >
          <Routes>
            <Route
              exact
              path="/"
              element={<Login login={() => setShowSidebar(true)} />}
            />
            <Route path="/register" element={<Register />} />
            <Route
              path="/forgot-password"
              element={<ForgotPassword />}
            />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route
              path="/home"
              element={(
                <ProtectedRoute>
                  <Home />
                </ProtectedRoute>
              )}
            />
            <Route
              path="/inventory"
              element={(
                <ProtectedRoute>
                  <Inventory />
                </ProtectedRoute>
              )}
            />
            <Route
              exact
              path="/dashboard"
              element={(
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              )}
            />
            <Route path="/no-license" element={<NoLicense sidebar={() => setShowSidebar(true)} />} />
            <Route
              path="/integrations"
              element={(
                <ProtectedRoute>
                  <Integrations />
                </ProtectedRoute>
              )}
            />
            <Route
              path="/organisation"
              element={(
                <ProtectedRoute>
                  <Organisation />
                </ProtectedRoute>
              )}
            />
            <Route
              path="/recommendation"
              element={(
                <ProtectedRoute>
                  <Recommendation />
                </ProtectedRoute>
              )}
            />
            <Route
              path="/pact-network"
              element={(
                <ProtectedRoute>
                  <SmartMetering />
                </ProtectedRoute>
              )}
            />
            <Route
              path="/saved-reports"
              element={(
                <ProtectedRoute>
                  <SavedReports />
                </ProtectedRoute>
              )}
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Box>
      </Box>

      <Footer />
      {environment === 'test' && <BugPanel />}
      <Loading />
      <Messagebar />
    </div>
  );
}

function App() {
  const environment = process.env.REACT_APP_ENVIRONMENT;
  const [authenticationObject, setAuthenticationObject] = useState(
    getAuthTokensFromBrowser(),
  );
  const shouldShowSidebar = authenticationObject.access_token !== ''
    && authenticationObject.organisation !== ''
    && authenticationObject.license_types.length !== 0;
  const [showSidebar, setShowSidebar] = useState(false);
  const authContextProviderValue = useMemo(
    () => ({
      authenticationObject,
      setAuthenticationObject,
    }),
    [authenticationObject, setAuthenticationObject],
  );
  return (
    <ThemeProvider theme={theme}>
      <AuthContext.Provider value={authContextProviderValue}>
        <LocationProvider>
          <DataProvider>
            <DashboardDataProvider>
              <BrowserRouter>
                <AppContent
                  showSidebar={showSidebar}
                  shouldShowSidebar={shouldShowSidebar}
                  setShowSidebar={setShowSidebar}
                  environment={environment}
                />
              </BrowserRouter>
            </DashboardDataProvider>
          </DataProvider>
        </LocationProvider>
      </AuthContext.Provider>
    </ThemeProvider>
  );
}

export default App;
